// ARKit52 viseme mapper — converts ElevenLabs character-level alignment
// into per-frame morph target weights for a MetaPerson (or any ARKit52-rigged)
// GLB. Used by `useLipSync.ts` Fase 3 hook.
//
// Pipeline (Fase 4-5 wires this in AvatarCore + MirrorChat):
//   1. ElevenLabs /with-timestamps → ElevenLabsAlignment
//   2. audio.currentTime → getActiveVisemeAt(t, alignment) → target weights
//   3. smoothViseme(prev, target, α) → per-frame weights
//   4. Apply to mesh.morphTargetInfluences[mesh.morphTargetDictionary[name]]
//
// Names are CASE-SENSITIVE per Apple's ARKit blendshape spec — they're
// looked up directly against the GLB's morphTargetDictionary, so any
// rename here would silently break lip sync without runtime errors.

export const ARKIT52_MORPHS = [
  "browDownLeft", "browDownRight", "browInnerUp", "browOuterUpLeft", "browOuterUpRight",
  "cheekPuff", "cheekSquintLeft", "cheekSquintRight",
  "eyeBlinkLeft", "eyeBlinkRight", "eyeLookDownLeft", "eyeLookDownRight",
  "eyeLookInLeft", "eyeLookInRight", "eyeLookOutLeft", "eyeLookOutRight",
  "eyeLookUpLeft", "eyeLookUpRight", "eyeSquintLeft", "eyeSquintRight",
  "eyeWideLeft", "eyeWideRight",
  "jawForward", "jawLeft", "jawOpen", "jawRight",
  "mouthClose", "mouthDimpleLeft", "mouthDimpleRight",
  "mouthFrownLeft", "mouthFrownRight", "mouthFunnel",
  "mouthLeft", "mouthLowerDownLeft", "mouthLowerDownRight",
  "mouthPressLeft", "mouthPressRight", "mouthPucker", "mouthRight",
  "mouthRollLower", "mouthRollUpper", "mouthShrugLower", "mouthShrugUpper",
  "mouthSmileLeft", "mouthSmileRight", "mouthStretchLeft", "mouthStretchRight",
  "mouthUpperUpLeft", "mouthUpperUpRight",
  "noseSneerLeft", "noseSneerRight", "tongueOut",
] as const;

export type ARKit52Morph = typeof ARKIT52_MORPHS[number];

export interface ElevenLabsAlignment {
  characters: string[];
  character_start_times_seconds: number[];
  character_end_times_seconds: number[];
  // SPEECH language of THIS utterance ("PT-BR" | "EN" | "NL"), stamped by the
  // TTS chokepoint (useVoice) from the per-utterance `speak(..., {language})`.
  // NOT the UI locale — the Idiomas/Professor module makes the avatar speak the
  // target language, which can differ from the UI. Rides on the alignment object
  // so it reaches useLipSync through the frozen avatar wrappers (which forward
  // the object by reference) WITHOUT touching AvatarCore. Optional: when absent
  // the PT-BR-only rules stay off (pre-#338 behavior) — see getActiveVisemeAt.
  language?: string;
}

// Char → viseme weights map. Tuned for PT-BR / EN / NL on Disney-standard
// phoneme groups. Weights are mouth-region only — eye/brow morphs stay 0
// during speech so they don't interfere with idle blinks or expressions.
const CHAR_TO_VISEME: Record<string, Partial<Record<ARKit52Morph, number>>> = {
  // Vowels — open
  "a": { jawOpen: 0.55 },
  "á": { jawOpen: 0.55 },
  "à": { jawOpen: 0.55 },
  "â": { jawOpen: 0.50 },
  "ã": { jawOpen: 0.45, mouthFunnel: 0.15 },
  "e": { jawOpen: 0.30, mouthSmileLeft: 0.12, mouthSmileRight: 0.12, mouthRollUpper: 0.10 },
  "é": { jawOpen: 0.35, mouthSmileLeft: 0.15, mouthSmileRight: 0.15, mouthRollUpper: 0.10 },
  "ê": { jawOpen: 0.30, mouthSmileLeft: 0.12, mouthSmileRight: 0.12, mouthRollUpper: 0.10 },
  "i": { jawOpen: 0.15, mouthSmileLeft: 0.20, mouthSmileRight: 0.20, mouthRollUpper: 0.10 },
  "í": { jawOpen: 0.15, mouthSmileLeft: 0.20, mouthSmileRight: 0.20, mouthRollUpper: 0.10 },
  "o": { jawOpen: 0.40, mouthFunnel: 0.40, mouthPucker: 0.20 },
  "ó": { jawOpen: 0.45, mouthFunnel: 0.50 },
  "ô": { jawOpen: 0.40, mouthFunnel: 0.45, mouthPucker: 0.25 },
  "õ": { jawOpen: 0.35, mouthFunnel: 0.40 },
  "u": { jawOpen: 0.20, mouthPucker: 0.55, mouthFunnel: 0.30 },
  "ú": { jawOpen: 0.20, mouthPucker: 0.60 },

  // Consonants — bilabials (closed lips)
  "p": { mouthClose: 0.70, jawOpen: 0.05 },
  "b": { mouthClose: 0.70, jawOpen: 0.05 },
  "m": { mouthClose: 0.75, jawOpen: 0.05 },

  // Labiodentals (lip-teeth)
  "f": { mouthLowerDownLeft: 0.30, mouthLowerDownRight: 0.30, mouthRollLower: 0.20, jawOpen: 0.10 },
  "v": { mouthLowerDownLeft: 0.30, mouthLowerDownRight: 0.30, mouthRollLower: 0.20, jawOpen: 0.10 },

  // Linguodentals / alveolars
  "t": { jawOpen: 0.20, mouthSmileLeft: 0.10, mouthSmileRight: 0.10 },
  "d": { jawOpen: 0.20, mouthSmileLeft: 0.10, mouthSmileRight: 0.10 },
  "s": { jawOpen: 0.15, mouthSmileLeft: 0.10, mouthSmileRight: 0.10 },
  "z": { jawOpen: 0.15, mouthSmileLeft: 0.10, mouthSmileRight: 0.10 },
  "n": { jawOpen: 0.20, mouthSmileLeft: 0.10, mouthSmileRight: 0.10 },
  "l": { jawOpen: 0.25, tongueOut: 0.10 },
  "r": { jawOpen: 0.25, mouthFunnel: 0.20 },

  // Velar
  "k": { jawOpen: 0.30 },
  "g": { jawOpen: 0.30 },

  // Sibilant clusters
  "x": { jawOpen: 0.25, mouthPucker: 0.25, mouthFunnel: 0.25 },
  "ç": { jawOpen: 0.15, mouthSmileLeft: 0.15, mouthSmileRight: 0.15 },
  "ñ": { jawOpen: 0.20, mouthSmileLeft: 0.10, mouthSmileRight: 0.10 },

  // Glides
  "y": { jawOpen: 0.20, mouthSmileLeft: 0.15, mouthSmileRight: 0.15, mouthRollUpper: 0.10 },
  "w": { jawOpen: 0.20, mouthPucker: 0.50 },
  "h": { jawOpen: 0.30 },
  "j": { jawOpen: 0.20, mouthSmileLeft: 0.15, mouthSmileRight: 0.15 },
  "q": { jawOpen: 0.25 },
};

const NEUTRAL_VISEME: Partial<Record<ARKit52Morph, number>> = {};

export function charToVisemeWeights(char: string): Partial<Record<ARKit52Morph, number>> {
  const lowered = char.toLowerCase();
  return CHAR_TO_VISEME[lowered] ?? NEUTRAL_VISEME;
}

// ── PT-BR grapheme-cluster layer ──────────────────────────────────────────────
// CHAR_TO_VISEME maps single graphemes. Portuguese breaks that in two ways the
// single-char table got wrong: (1) digraphs (ch/nh/lh/rr/ss/qu/gu) whose real
// mouth shape is NOT the sum of their letters, and (2) `c`, which was left
// UNMAPPED entirely → NEUTRAL (a closed mouth) on a very common letter. This
// layer resolves a character's viseme IN CONTEXT — peeking one grapheme left/
// right within the alignment — without changing getActiveVisemeAt(t, alignment).
//
// Language-agnostic on purpose: `c`→/s/ before a front vowel else /k/, and the
// digraph clusters, are correct for PT and harmless for EN/NL, so NO `language`
// parameter is needed — which keeps AvatarCore/AvatarViewer (frozen) untouched.

// Digraph → ONE viseme, shown across BOTH characters' time windows so the pair
// reads as a single mouth shape. Keys are the two lowercased graphemes joined.
const DIGRAPH_VISEME: Record<string, Partial<Record<ARKit52Morph, number>>> = {
  // ch /ʃ/ — sibilant: lips pushed forward + rounded (not neutral + jaw drop).
  ch: { jawOpen: 0.16, mouthFunnel: 0.35, mouthPucker: 0.30 },
  // nh /ɲ/ — palatal nasal: low jaw + light spread, one shape (not n + h).
  nh: { jawOpen: 0.20, mouthSmileLeft: 0.10, mouthSmileRight: 0.10 },
  // lh /ʎ/ — palatal lateral: light spread, NO crude tongueOut of a lone `l`.
  lh: { jawOpen: 0.18, mouthSmileLeft: 0.08, mouthSmileRight: 0.08 },
  // rr /ʁ/ — guttural R (throat): moderately open, lips near-neutral.
  rr: { jawOpen: 0.28, mouthFunnel: 0.10 },
  // ss /s/ — collapse the geminate to one /s/ shape (mirror of `s`).
  ss: CHAR_TO_VISEME["s"],
  // qu /k/ — the u is silent (que/qui); use /k/ so the lone-`u` strong pucker
  // never fires on a silent vowel.
  qu: CHAR_TO_VISEME["k"],
  // gu /g/ — silent u (gue/gui); same reasoning as qu.
  gu: CHAR_TO_VISEME["g"],
};

// `c` is soft (/s/) before these front vowels (incl. accented), hard (/k/) else.
const C_SOFT_NEXT = new Set(["e", "é", "ê", "i", "í"]);

// ── Shared PT-BR context helpers (used by BOTH the ARKit and native layers) ────
// Four Brazilian-Portuguese rules need one more grapheme of context than the
// digraph/`c` logic already reads. All are pure peek — no engine, no language
// param (see the caveat in the PR: these are PT-specific and misfire on EN/NL).
//
//   1. Coda /l/ vocalizes to [w]        — Brasil, sol, alto  → rounded, not DD
//   2. Coda m/n nasalize the vowel      — bom, sim, canto    → NO lip closure
//   3. di/ti palatalize to [dʒ]/[tʃ]    — dia, noite, verdade→ affricate, not DD
//   4. g before a front vowel is [ʒ]    — gente, longe       → CH, not kk

// Vowel graphemes (incl. accented). A consonant is "coda" when the NEXT grapheme
// is not a vowel (i.e. a consonant or a word boundary); "onset" when it is.
const VOWELS = new Set([
  "a", "á", "à", "â", "ã", "e", "é", "ê", "i", "í",
  "o", "ó", "ô", "õ", "u", "ú", "ü", "y",
]);

// Coda context = next grapheme is NOT a vowel. Empty (word edge), space and
// punctuation are all non-vowels, so this also captures word-final position —
// reusing the same "boundary occupies the adjacent slot" fact the digraph logic
// relies on. Drives rules 1 & 2.
function isCodaContext(next: string): boolean {
  return !VOWELS.has(next);
}

// A word edge: empty slot (start/end of the alignment) or any non-letter
// (space / punctuation / digit). Used to detect word-final `e`.
function isBoundary(ch: string): boolean {
  return ch === "" || !/[a-zà-ÿç]/i.test(ch);
}

// `next` triggers t/d palatalization (rule 3): a written i/í, OR a word-final
// e/ê (which reduces to [i] in BP → same palatalization). The e-final case peeks
// TWO graphemes ahead (`afterNext` must be a boundary). `é` (stressed open [ɛ])
// does not reduce, so it's excluded.
function palatalizesTD(next: string, afterNext: string): boolean {
  return (
    next === "i" || next === "í" ||
    ((next === "e" || next === "ê") && isBoundary(afterNext))
  );
}

// Rule targets for the ARKit channel (Avaturn). The affricate reuses the
// existing protruded `ch` sibilant shape; coda /l/ reuses a rounded pucker.
const ARKIT_AFFRICATE: Partial<Record<ARKit52Morph, number>> = DIGRAPH_VISEME["ch"];
const ARKIT_CODA_L: Partial<Record<ARKit52Morph, number>> = {
  jawOpen: 0.12, mouthPucker: 0.45, mouthFunnel: 0.28,
};

// Resolve the viseme for character `idx` using its neighbours. Order:
//   1. digraph (cur+next, then prev+cur) → the shared cluster shape;
//   2. contextual `c` (→ /s/ before a front vowel, else /k/);
//   3. plain single-grapheme lookup.
// Only characters[idx-1 .. idx+1] are read. A word boundary (space/punctuation)
// occupies the adjacent slot and so breaks any cluster match — boundaries are
// respected without an explicit check.
// `ptBr` gates the PT-BR-SPECIFIC rules (2/3/4). They're wrong for EN/NL —
// English coda-m closes the lips (time/come), ti/di don't palatalize (tip),
// ge/gi is often hard (get) — so they fire ONLY when the utterance is PT-BR.
// Rule 1 (coda /l/ → rounded) is acceptable cross-language and stays global.
function resolveVisemeAt(
  characters: string[],
  idx: number,
  ptBr: boolean,
): Partial<Record<ARKit52Morph, number>> {
  const cur = (characters[idx] ?? "").toLowerCase();
  const next = (characters[idx + 1] ?? "").toLowerCase();
  const prev = (characters[idx - 1] ?? "").toLowerCase();
  const afterNext = (characters[idx + 2] ?? "").toLowerCase();

  // Digraph where cur is the FIRST letter, then where cur is the SECOND letter.
  // Both windows return the same cluster shape (one viseme across the pair).
  const asFirst = DIGRAPH_VISEME[cur + next];
  if (asFirst) return asFirst;
  const asSecond = DIGRAPH_VISEME[prev + cur];
  if (asSecond) return asSecond;

  // Contextual `c` (c+h is already handled above as the `ch` cluster).
  if (cur === "c") {
    return C_SOFT_NEXT.has(next) ? CHAR_TO_VISEME["s"] : CHAR_TO_VISEME["k"];
  }

  // Rules 2/3/4 — PT-BR only (gated). See the note above `resolveVisemeAt`.
  if (ptBr) {
    // Rule 4 — g before a front vowel is /ʒ/ (gente, longe): protruded, not velar.
    // AFTER the `gu` digraph above, so guerra/guia keep the hard /g/.
    if (cur === "g" && C_SOFT_NEXT.has(next)) return ARKIT_AFFRICATE;

    // Rule 3 — di/ti palatalize to /dʒi/,/tʃi/ (dia, noite, verdade).
    if ((cur === "d" || cur === "t") && palatalizesTD(next, afterNext)) return ARKIT_AFFRICATE;

    // Rule 2 — coda m/n nasalize the vowel WITHOUT closing the lips (bom, sim,
    // canto). Suppress the labial → neutral; the vowel lingers via smoothing.
    if ((cur === "m" || cur === "n") && isCodaContext(next)) return NEUTRAL_VISEME;
  }

  // Rule 1 — coda /l/ vocalizes to [w] (Brasil, sol, alto): rounded, not dental.
  // GLOBAL: acceptable in EN/NL (dark coda-l is already velarized/rounded).
  if (cur === "l" && isCodaContext(next)) return ARKIT_CODA_L;

  return charToVisemeWeights(cur);
}

// Returns the viseme weights active at `currentTimeSeconds`.
// Uses binary search on `character_end_times_seconds` (assumed monotonic
// non-decreasing per ElevenLabs contract). If the time falls in the gap
// before the first char or after the last char's end, returns NEUTRAL.
export function getActiveVisemeAt(
  currentTimeSeconds: number,
  alignment: ElevenLabsAlignment,
): Partial<Record<ARKit52Morph, number>> {
  if (!alignment.characters?.length) return NEUTRAL_VISEME;

  const ends = alignment.character_end_times_seconds;
  let lo = 0;
  let hi = ends.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (ends[mid] < currentTimeSeconds) lo = mid + 1;
    else hi = mid;
  }
  const idx = lo;
  const start = alignment.character_start_times_seconds[idx];
  const end = alignment.character_end_times_seconds[idx];
  if (currentTimeSeconds < start || currentTimeSeconds > end) return NEUTRAL_VISEME;
  // PT-BR rules gate on the utterance's SPEECH language (strict). Absent/EN/NL
  // → false → pre-#338 behavior for rules 2/3/4 (rule 1 is unconditional).
  return resolveVisemeAt(alignment.characters, idx, alignment.language === "PT-BR");
}

// ── MetaPerson NATIVE-viseme channel ──────────────────────────────────────────
// The bug this fixes: everything above drives ARKit blendshape NAMES
// (mouthClose/mouthFunnel/mouthPucker…). Avaturn/RPM GLBs expose those, so lip
// sync works there. MetaPerson's default export instead ships 15 NON-prefixed
// native visemes (PP/FF/TH/…/ou) as its OWN mouth channel and — apart from the
// single shared `jawOpen` — carries NONE of the ARKit mouth names. So every
// ARKit write was a silent no-op on MetaPerson (`if (idx === undefined)
// continue` in useLipSync), leaving only jawOpen → "mouth moves, shapes wrong".
//
// This layer is the native twin of the ARKit layer above: same grapheme /
// digraph / contextual-`c` resolution, but it emits native viseme names.
// useLipSync FEATURE-DETECTS which channel a loaded avatar exposes and drives
// exactly ONE of them — never both. Nothing here is wired to a `language`
// param, so AvatarCore/AvatarViewer (frozen) stay untouched.

// The 15 MetaPerson native visemes (Oculus/Meta set, non-prefixed). `sil` is
// the rest pose; an empty target decays every viseme to 0, i.e. sil — so `sil`
// never has to be written explicitly (mirrors ARKit NEUTRAL).
export const NATIVE_VISEME_MORPHS = [
  "sil", "PP", "FF", "TH", "DD", "kk", "CH", "SS",
  "nn", "RR", "aa", "E", "ih", "oh", "ou",
] as const;

export type NativeVisemeMorph = typeof NATIVE_VISEME_MORPHS[number];

// Native visemes are COMPLETE authored mouth poses (each already encodes its own
// jaw drop / lip rounding), so a grapheme maps to ONE viseme at a base weight.
// Weights are moderate on purpose — the hook further multiplies by VISEME_INTENSITY
// (0.8) — so the most open vowel peaks well below a photoreal "shout". Closures
// (PP) run near full so bilabials actually read as lips-together.
const CHAR_TO_NATIVE: Record<string, Partial<Record<NativeVisemeMorph, number>>> = {
  // Vowels — open / mid / close
  "a": { aa: 0.85 }, "á": { aa: 0.85 }, "à": { aa: 0.85 }, "â": { aa: 0.80 }, "ã": { aa: 0.78 },
  "e": { E: 0.65 }, "é": { E: 0.70 }, "ê": { E: 0.65 },
  "i": { ih: 0.55 }, "í": { ih: 0.55 },
  "o": { oh: 0.80 }, "ó": { oh: 0.85 }, "ô": { oh: 0.80 }, "õ": { oh: 0.75 },
  "u": { ou: 0.85 }, "ú": { ou: 0.85 },

  // Bilabials — full lip closure (p/b/m)
  "p": { PP: 1.00 }, "b": { PP: 1.00 }, "m": { PP: 1.00 },

  // Labiodentals — lip-teeth (f/v)
  "f": { FF: 0.85 }, "v": { FF: 0.85 },

  // Dentals / alveolars (t/d/l)
  "t": { DD: 0.60 }, "d": { DD: 0.60 }, "l": { DD: 0.60 },

  // Sibilants (s/z, and ç → /s/)
  "s": { SS: 0.70 }, "z": { SS: 0.70 }, "ç": { SS: 0.70 },

  // Nasal (n; nh/lh handled as digraphs)
  "n": { nn: 0.60 }, "ñ": { nn: 0.60 },

  // Rhotic (r; rr handled as digraph)
  "r": { RR: 0.70 },

  // Velar (k/g; c-hard/qu/gu resolved contextually / as digraphs)
  "k": { kk: 0.60 }, "g": { kk: 0.60 }, "q": { kk: 0.60 },

  // Postalveolar affricate/fricative (x/j → /ʃ,ʒ/; ch handled as digraph)
  "x": { CH: 0.80 }, "j": { CH: 0.80 },

  // Glides
  "y": { ih: 0.55 }, "w": { ou: 0.85 },

  // Aspirate `h` is silent in PT → rest (sil).
  "h": {},
};

const NEUTRAL_NATIVE: Partial<Record<NativeVisemeMorph, number>> = {};

// Digraph → ONE native viseme, mirroring DIGRAPH_VISEME so the same PT clusters
// resolve on the native channel. `th` is added for EN (no PT single-grapheme
// maps to TH, so the viseme would otherwise never fire).
const DIGRAPH_NATIVE: Record<string, Partial<Record<NativeVisemeMorph, number>>> = {
  ch: { CH: 0.80 }, // /ʃ/
  nh: { nn: 0.60 }, // /ɲ/ palatal nasal
  lh: { nn: 0.60 }, // /ʎ/ palatal lateral → nasal-family shape
  rr: { RR: 0.75 }, // /ʁ/ guttural R
  ss: { SS: 0.70 }, // geminate /s/
  qu: { kk: 0.60 }, // silent u → /k/
  gu: { kk: 0.60 }, // silent u → /g/
  th: { TH: 0.70 }, // EN dental fricative
};

// Native rule targets (same four PT-BR rules as the ARKit layer). Affricate
// [dʒ]/[tʃ]/[ʒ] → CH (protruded); coda /l/ [w] → `ou` (rounded glide).
const NATIVE_AFFRICATE: Partial<Record<NativeVisemeMorph, number>> = { CH: 0.75 };
const NATIVE_CODA_L: Partial<Record<NativeVisemeMorph, number>> = { ou: 0.72 };

// Native twin of resolveVisemeAt: same neighbour-peek order (digraph → soft/hard
// `c` → PT-BR context rules → single grapheme), emitting native viseme names.
// `ptBr` gates rules 2/3/4 exactly as in resolveVisemeAt (PT-BR only); rule 1
// (coda /l/) is global. Kept in lockstep with the ARKit twin.
function resolveNativeVisemeAt(
  characters: string[],
  idx: number,
  ptBr: boolean,
): Partial<Record<NativeVisemeMorph, number>> {
  const cur = (characters[idx] ?? "").toLowerCase();
  const next = (characters[idx + 1] ?? "").toLowerCase();
  const prev = (characters[idx - 1] ?? "").toLowerCase();
  const afterNext = (characters[idx + 2] ?? "").toLowerCase();

  const asFirst = DIGRAPH_NATIVE[cur + next];
  if (asFirst) return asFirst;
  const asSecond = DIGRAPH_NATIVE[prev + cur];
  if (asSecond) return asSecond;

  // `c` soft (/s/ → SS) before a front vowel, hard (/k/ → kk) otherwise.
  if (cur === "c") {
    return C_SOFT_NEXT.has(next) ? { SS: 0.70 } : { kk: 0.60 };
  }

  // Rules 2/3/4 — PT-BR only (gated).
  if (ptBr) {
    // Rule 4 — g before a front vowel is /ʒ/ (gente, longe). After `gu` digraph.
    if (cur === "g" && C_SOFT_NEXT.has(next)) return NATIVE_AFFRICATE;

    // Rule 3 — di/ti palatalize to /dʒi/,/tʃi/ (dia, noite, verdade).
    if ((cur === "d" || cur === "t") && palatalizesTD(next, afterNext)) return NATIVE_AFFRICATE;

    // Rule 2 — coda m/n nasalize the vowel WITHOUT closing the lips (bom, sim,
    // canto). Return neutral so the preceding vowel lingers via smoothing.
    if ((cur === "m" || cur === "n") && isCodaContext(next)) return NEUTRAL_NATIVE;
  }

  // Rule 1 — coda /l/ vocalizes to [w] (Brasil, sol, alto): rounded. GLOBAL.
  if (cur === "l" && isCodaContext(next)) return NATIVE_CODA_L;

  return CHAR_TO_NATIVE[cur] ?? NEUTRAL_NATIVE;
}

// Native twin of getActiveVisemeAt — identical binary search + windowing, native
// names out. Kept as a separate function (not a shared generic) so the ARKit
// path above stays byte-for-byte unchanged and its Avaturn behaviour can't
// regress. useLipSync calls exactly one of the two per avatar.
export function getActiveNativeVisemeAt(
  currentTimeSeconds: number,
  alignment: ElevenLabsAlignment,
): Partial<Record<NativeVisemeMorph, number>> {
  if (!alignment.characters?.length) return NEUTRAL_NATIVE;

  const ends = alignment.character_end_times_seconds;
  let lo = 0;
  let hi = ends.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (ends[mid] < currentTimeSeconds) lo = mid + 1;
    else hi = mid;
  }
  const idx = lo;
  const start = alignment.character_start_times_seconds[idx];
  const end = alignment.character_end_times_seconds[idx];
  if (currentTimeSeconds < start || currentTimeSeconds > end) return NEUTRAL_NATIVE;
  return resolveNativeVisemeAt(alignment.characters, idx, alignment.language === "PT-BR");
}

// Linear interpolation toward `target` weights. Returns a complete record
// over all ARKIT52_MORPHS so the caller can blindly write each index into
// morphTargetInfluences without missing-key checks. Untouched morphs decay
// to 0 because `target[morph] ?? 0`.
export function smoothViseme(
  current: Partial<Record<ARKit52Morph, number>>,
  target: Partial<Record<ARKit52Morph, number>>,
  alpha = 0.3,
): Record<ARKit52Morph, number> {
  const result = {} as Record<ARKit52Morph, number>;
  for (const morph of ARKIT52_MORPHS) {
    const cur = current[morph] ?? 0;
    const tgt = target[morph] ?? 0;
    result[morph] = cur + (tgt - cur) * alpha;
  }
  return result;
}
